<template>
  <div style="height: 100%">
    <label style="padding: 20px 0px 10px 30px;">列头柜输入电压</label>
    <div v-if="chartDataList.length > 0" style="height: 85%">
      <!-- 5个及以下设备直接显示 -->
      <template v-if="chartDataList.length === 1">
        <div style="height: 100%">
          <CabinetBarChart :data="chartDataList[0]" style="height: 100%;"></CabinetBarChart>
        </div>
      </template>
      <!-- 超过5个设备使用轮播 -->
      <template v-else>
        <el-carousel 
          :interval="10000" 
          indicator-position="none" 
          :autoplay="true" 
          style="height: 100%;z-index: 0">
          <el-carousel-item v-for="(chartData, index) in chartDataList" :key="index">
            <CabinetBarChart :data="chartData" style="height: 100%;"></CabinetBarChart>
          </el-carousel-item>
        </el-carousel>
      </template>
    </div>
    <div v-else style="height: 100%;padding-top: 20%">
      <no-data></no-data>
    </div>
  </div>
</template>

<script>
import CabinetBarChart from "@/components/echarts/CabinetBarChart";
import NoData from "@/components/NoData";
import { getSpecialTypeValues } from "@/network/measuring-point-history";

export default {
  name: "controlRoomCabinetBar",
  components: { CabinetBarChart, NoData },
  data() {
    return {
      chartDataList: [],
      pageSize: 3, // Each device has 6 bars, so maybe show fewer devices per page? 3 devices = 18 bars (ok), 5 devices = 30 bars (too many). Let's try 3.
      deviceType: 'PM_GGDS', // 列头柜类型
      // A路A相电压, A路B相电压, A路C相电压, B路A相电压, B路B相电压, B路C相电压
      pointCodes: 'm1_a_u,m1_b_u,m1_c_u,m2_a_u,m2_b_u,m2_c_u'
    };
  },
  mounted() {
    this.getData();
  },
  created() {
    this.$store.dispatch('addIntervalObj', { 'getCabinetBarData:10000': this.getData })
  },
  destroyed() {
    this.$store.dispatch('removeIntervalObj', 'getCabinetBarData')
  },
  methods: {
    getData() {
      getSpecialTypeValues(this.deviceType, this.pointCodes).then(res => {
        if (res.code === 0 && res.data) {
          const { devices, devicePointNames, devicePointValues, thresholdValues } = res.data;
          if (devices && devices.length > 0) {
            const validData = this.processData(devices, devicePointValues);
            const thresholds = this.processThresholds(thresholdValues, devicePointNames);
            
            // 获取测点名称
            // devicePointNames[0] should allow access to names for the first device
            const firstDevicePoints = (devicePointNames && devicePointNames[0]) || [];
            
            // Fallback names
            const pointNames = [
              firstDevicePoints[0] || 'A路A相电压',
              firstDevicePoints[1] || 'A路B相电压',
              firstDevicePoints[2] || 'A路C相电压',
              firstDevicePoints[3] || 'B路A相电压',
              firstDevicePoints[4] || 'B路B相电压',
              firstDevicePoints[5] || 'B路C相电压'
            ];

            if (validData.length > 0) {
              this.chartDataList = this.chunkData(validData, this.pageSize, thresholds, pointNames);
            } else {
              this.chartDataList = [];
            }
          } else {
            this.chartDataList = [];
          }
        } else {
          this.chartDataList = [];
        }
      }).catch(() => {
        this.chartDataList = [];
      });
    },
    // 处理接口返回的数据
    processData(devices, devicePointValues) {
      const result = [];
      for (let i = 0; i < devices.length; i++) {
        const device = devices[i];
        const values = devicePointValues[i];
        if (device && values && values.length > 0) {
          result.push({
            deviceName: device.name || '未知设备',
            status: device.status,
            m1_a_u: values[0] || 0,
            m1_b_u: values[1] || 0,
            m1_c_u: values[2] || 0,
            m2_a_u: values[3] || 0,
            m2_b_u: values[4] || 0,
            m2_c_u: values[5] || 0
          });
        }
      }
      return result;
    },
    // 处理阈值数据
    processThresholds(thresholdValues, devicePointNames) {
      if (!thresholdValues || thresholdValues.length === 0) return null;
      const result = {};
      
      const firstDevicePoints = (devicePointNames && devicePointNames[0]) || [];
      const names = [
        firstDevicePoints[0] || 'A路A相电压',
        firstDevicePoints[1] || 'A路B相电压',
        firstDevicePoints[2] || 'A路C相电压',
        firstDevicePoints[3] || 'B路A相电压',
        firstDevicePoints[4] || 'B路B相电压',
        firstDevicePoints[5] || 'B路C相电压'
      ];
      
      // key names map to what CabinetBarChart expects
      const keys = ['m1_a', 'm1_b', 'm1_c', 'm2_a', 'm2_b', 'm2_c'];
      
      keys.forEach((key, index) => {
        if (thresholdValues[index] && thresholdValues[index].length > 0) {
          result[key] = thresholdValues[index].map(item => ({
            alarmHigh: item.alarmHigh,
            alarmLow: item.alarmLow,
            levelColor: item.alarmLevel ? item.alarmLevel.levelColor : '#ff0000',
            levelName: item.alarmLevel ? item.alarmLevel.levelName : '',
            pointName: names[index]
          }));
        }
      });

      return result;
    },
    // 将数据分组
    chunkData(data, size, thresholds, pointNames) {
      const result = [];
      for (let i = 0; i < data.length; i += size) {
        const chunk = data.slice(i, i + size);
        result.push({
          deviceNames: chunk.map(item => item.deviceName),
          statuses: chunk.map(item => item.status),
          m1_a_u: chunk.map(item => item.m1_a_u),
          m1_b_u: chunk.map(item => item.m1_b_u),
          m1_c_u: chunk.map(item => item.m1_c_u),
          m2_a_u: chunk.map(item => item.m2_a_u),
          m2_b_u: chunk.map(item => item.m2_b_u),
          m2_c_u: chunk.map(item => item.m2_c_u),
          thresholds: thresholds,
          pointNames: pointNames
        });
      }
      return result;
    }
  }
}
</script>

<style scoped>
label {
  font-weight: bold;
  color: #01EFFD;
}

.el-carousel >>> .el-carousel__arrow {
  top: 5%;
}
</style>
